// The campaign builder's state: every hook the wizard calls, and what it derives.

import { countInFlightOperations } from '@/lib/creative-slot-view';
import { campaignStatusLine } from '@/lib/campaign-status-line';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useOutsideClick } from '@/components/dropdowns';
import { useAudiences, useCampaignOverview, useCreatives, useProductions } from '@/api/hooks';
import { useCampaignFlightExtensions, useArchiveCampaign, useAudienceEstimateSize, useCampaignForecast, useCampaignPreflight, useCreateAudience, useCreateCampaign, useCreateCreative, useDeleteCampaign, useDuplicateCampaign, useRetryCampaignSync, useLaunchCampaign, useLaunchWhenReady, useCancelLaunchWhenReady, useMetaDeliveryContract, useCampaignSyncStatus, campaignSyncSettled, useApproveCreative, usePatchCampaign, usePatchCreative, usePauseCampaign, useResumeCampaign, useSubmitCreativeForReview, useUpdateCampaignPlan } from '@/api/manage-hooks';
import { useQueryClient } from '@tanstack/react-query';
import { armedWaitingReason, campaignStatusKind } from '@/lib/campaign-status';
import { campaignPublishSequence, deliveryTargetFor } from '@/lib/campaign-publish-sequence';
import { useRequiredFeedback } from '@/components/required-field';
import { unresolvedCreativeSlots } from '@/lib/campaign-creative-pins';
import { defaultGroupsForType } from './audience';
import { fmtDateInput, dayYmd, nyStartOfDayMs } from '@/lib/datetime';
import { adaptProductionForLegacyManage, type LegacyManageCampaign, type LegacyManageProduction } from '@/lib/manage-screen-adapters';
import type { Audience, Campaign, CampaignPlan, CampaignStatus, Creative } from '@/api/types';
import type { AudiencePreviewInput, CampaignPreflight } from '@/api/manage-types';
import type { AudienceSyncGateItem } from '@/components/audience-sync-gate';
import type { CreativeSyncGateItem } from '@/components/creative-sync-gate';
import type { AudiencePreviewState } from './audience';
import type { CampaignFlight } from '@/lib/campaign-flights';
import type { MaterializedCreative } from './create-flow-save';
import type { CreativeSlot, CtvSlotData, DspSlotData, MetaAssetsBySlot, MetaCreativeSlot } from '@/lib/creative-required-issues';
import { resolveVisualAudienceRules } from '@/lib/audience-visual-rules';
import { audienceSlotsFingerprint, audienceSlotsFromPlan, allAudienceSlotsConfirmed, type CampaignAudienceSlot } from '@/lib/campaign-audience-slots';
import { firstLookalikeShareBps } from '@/lib/lookalike-model';
import { useVenueNames } from './create-production-picker';
import { mirrorSoleUntouchedFlight } from '@/lib/campaign-flights';
import { creativeSlotsFromPlan, creativeDataFromSlots, localAssetFingerprint, planCreativesAllApproved, campaignCreativeSelections } from '@/lib/campaign-plan';
import { equalAudienceAllocations } from './create-audience-slot';
import { settledAudienceSize, providerLabel, usePostLaunchSyncSnapshot, type PublishProgress } from './create-publishing-modal';
import { usePlanTermsRefresh } from './create-flow-plan-refresh';
import { flightExtensionSignal } from '@/lib/flight-extension';
import { campaignPlan as campaignPlanResource } from '@/api/resources';
import { useCan } from '@/auth/capabilities';

/** The saved campaign as the edit route hands it over: the legacy edit shape,
 *  plus the plan revision and pins it was read at. */
export interface CreateFlowCampaign extends LegacyManageCampaign {
  revision: number;
  audienceSegments: CampaignPlan['audience_segments'];
  creativeIds: string[];
}

/** The wizard's sections, in order; each opens and closes on its own. */
export type CreateFlowSection = 'production' | 'obj' | 'ch' | 'aud' | 'cr' | 'prev' | 'sched' | 'rev';

/** The pause or resume the operator can undo from the toast. */
export interface StatusUndoToast {
  from: CampaignStatus;
  to: 'live' | 'paused';
  key: number;
}

/** Saved, now waiting for every audience revision to materialize; the creatives
 *  wait next, in `CreativeSyncGateState`. */
export interface AudienceSyncGateState {
  campaignId: string;
  items: AudienceSyncGateItem[];
  creativeItems: CreativeSyncGateItem[];
}

export interface CreativeSyncGateState {
  campaignId: string;
  items: CreativeSyncGateItem[];
}

export interface CreateFlowProps {
  /** Absent for a new campaign. */
  campaign?: CreateFlowCampaign;
  plan?: CampaignPlan;
  setView: (view: string) => void;
  /** The pickers' lists; the wizard reads its own queries when they are absent. */
  audiences?: Audience[];
  creatives?: Creative[];
  /** The exact revisions the plan pins, which the list may not hold. */
  pinnedCreatives?: Creative[];
}

export function useCreateFlow({
  campaign,
  plan,
  setView,
  audiences,
  creatives,
  pinnedCreatives,
}: CreateFlowProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: productionRows = [] } = useProductions();
  const { data: fetchedAudiences = [] } = useAudiences();
  const { data: fetchedCreatives = [] } = useCreatives();
  const availableAudiences = audiences ?? fetchedAudiences;
  const availableCreatives = creatives ?? fetchedCreatives;
  const initialPinnedCreatives = pinnedCreatives ?? availableCreatives;
  const venueNames = useVenueNames();
  const PRODUCTIONS = useMemo(
    () => productionRows.map(production => adaptProductionForLegacyManage(production, venueNames)),
    [productionRows, venueNames],
  );

  // ── Post-draft lock state ───────────────────────────────────────────────
  // The revision-aware plan is editable only while status is draft.
  const [status, setStatus] = useState(campaign?.status || 'draft');
  const wasLive = status !== 'draft';

  // "Today" anchor (NY calendar day) for past / current / future flight
  // derivation. `today` is the UTC-midnight instant of NY's current day so the
  // calendar arithmetic below stays independent of the viewer's browser tz.
  const todayStr = fmtDateInput(new Date());
  const today = useMemo(() => new Date(todayStr), [todayStr]);

  const [production, setShow] = useState<LegacyManageProduction | null>(
    campaign ? PRODUCTIONS.find(s => s.id === campaign.production) ?? null : null,
  );
  const [channel, setChannel] = useState<Campaign['channel'] | null>(campaign?.channel || null);
  const metaContractQuery = useMetaDeliveryContract(channel === 'meta');
  const metaContract = channel === 'meta' ? metaContractQuery.data : null;
  const [budget, setBudget] = useState(campaign?.budget || 30000);
  const [days, setDays] = useState(campaign?.days || 14);

  const initialStartISO = useMemo(() => {
    return campaign?.startDate || todayStr;
  }, [campaign, todayStr]);

  const initialEndISO = useMemo(() => {
    if (campaign?.endDate) return campaign.endDate;
    const start = new Date(initialStartISO);
    start.setUTCDate(start.getUTCDate() + (campaign?.days || 14));
    return dayYmd(start);
  }, [initialStartISO, campaign]);

  // The server's ceiling is `today_nyc + 180 calendar days`. A flat 180*24h
  // offset from "now" drifts by an hour across a DST boundary and can offer a
  // date the API then rejects, so anchor at NY midnight and add a half-day
  // cushion before snapping back to a calendar day.
  const maxEndISO = fmtDateInput(nyStartOfDayMs(Date.now()) + 180*864e5 + 12*3600e3);
  const [startDate, setStartDate] = useState(initialStartISO);
  const [endDate, setEndDate] = useState(initialEndISO);
  useEffect(() => {
    const s = new Date(startDate).getTime();
    const e = new Date(endDate).getTime();
    if (!isNaN(s) && !isNaN(e) && e >= s) setDays(Math.max(1, Math.round((e - s) / 864e5)));
  }, [startDate, endDate]);
  const [name, setName] = useState(campaign?.name || '');
  // The name the server holds, so a blur that changed nothing sends nothing.
  const [savedName, setSavedName] = useState(campaign?.name || '');
  const [renaming, setRenaming] = useState(false);
  const patchCampaign = usePatchCampaign();
  const [initialAudienceState] = useState(() =>
    audienceSlotsFromPlan(plan, availableAudiences, productionRows, defaultGroupsForType));
  // One slot per campaign audience: slot i is `audience_segments[i]`, which is
  // one ad set, one delivery group, and one reporting position. The type lives
  // on the slot rather than on the campaign, which is the whole feature.
  const [audienceSlots, setAudienceSlots] = useState(initialAudienceState.slots);
  // Segments whose pinned definition would not resolve. Saving while this is
  // non-empty would write a SHORTER plan than the one on record, orphaning a
  // live provider ad set, so it refuses instead.
  const unresolvedSegmentPositions = initialAudienceState.unresolvedPositions;
  // Preview snapshots live beside the slots, not inside them: an estimate
  // arriving must not dirty the save fingerprint.
  const [slotPreviews, setSlotPreviews] = useState<Record<string, AudiencePreviewState>>({});
  const [slotConfirmed, setSlotConfirmed] = useState<Record<string, boolean>>(() => Object.fromEntries(
    initialAudienceState.slots.map((slot) => [slot.id, !!plan?.audience_segments?.length]),
  ));

  // Patch one slot. MUST be functional: `AudienceBuilder.onTypeChange` calls
  // setType and setGroups as two separate updates, and with every slot in one
  // array a stale closure would silently drop whichever landed second.
  const patchSlot = useCallback((id: string, patch: Partial<CampaignAudienceSlot>) => {
    setAudienceSlots((current) => current.map(
      (slot) => (slot.id === id ? { ...slot, ...patch } : slot),
    ));
  }, []);
  // Each slot's resolved rules, keyed off the slot itself rather than a
  // campaign-level type, so a mixed campaign sizes each slot by its own kind.
  const slotEncodings = useMemo(() => audienceSlots.map((slot) => {
    try {
      return resolveVisualAudienceRules(slot.groups, {
        productions: productionRows,
        audiences: availableAudiences,
      });
    } catch {
      return null;
    }
  }), [audienceSlots, availableAudiences, productionRows]);
  // PER SLOT, never over the union. Scanning the union is the defect this
  // replaces: one lookalike leaf anywhere routed the whole campaign to
  // `prospect_preview`, which answers with the lookalike's size alone.
  const prospectingSlots = useMemo(
    () => slotEncodings.map((encoded) => !!encoded && firstLookalikeShareBps(encoded.rules) !== null),
    [slotEncodings],
  );
  const anySlotProspecting = prospectingSlots.some(Boolean);
  const audConfirmed = allAudienceSlotsConfirmed(audienceSlots, slotConfirmed);
  const setAudConfirmed = useCallback((next: boolean) => {
    setSlotConfirmed((current) => Object.fromEntries(
      Object.keys(current).map((id) => [id, next]),
    ));
  }, []);
  const [creConfirmed, setCreConfirmed] = useState(!!plan?.creatives?.length);
  // Rehydrated from the plan's creative statuses: a saved wizard whose
  // creatives are all approved had this step confirmed when it saved (that is
  // the only path that approves them), and the backend preflight gates on the
  // same fact. Without this, a reload silently un-reviewed everything.
  const [prevConfirmed, setPrevConfirmed] = useState(() =>
    campaign?.status === 'ready_to_publish'
    || planCreativesAllApproved(plan, initialPinnedCreatives),
  );
  // Which creative set the sign-off covers. `prevConfirmed` alone is a sticky
  // boolean, and saving auto-approves every draft creative when it is set —
  // so a rehydrated confirmation must stop applying the moment the creatives
  // change, or a creative added later is approved without anyone reviewing it.
  const [reviewedCreativeFingerprint, setReviewedCreativeFingerprint] = useState<string | null>(null);
  const [previewCreativeIds, setPreviewCreativeIds] = useState(
    () => plan?.creatives?.map((creative) => creative.creative_id) ?? [],
  );
  const [schConfirmed, setSchConfirmed] = useState(!!plan);
  // Seeded once, like the audience slots above and for the same reason: the
  // legacy edit state is a snapshot. Each channel's seed is computed a single
  // time so the slots and the assets derived from them cannot disagree.
  const [initialCreativeState] = useState(() => ({
    meta: creativeSlotsFromPlan(plan, 'mc', 'Creative', initialPinnedCreatives),
    ctv: creativeSlotsFromPlan(plan, 'ctv', 'Creative', initialPinnedCreatives),
    dsp: creativeSlotsFromPlan(plan, 'dg', 'Banner Group', initialPinnedCreatives),
  }));
  // Creative pins that would not resolve. Saving while this is non-empty would
  // write a plan pinning creatives we could not read, and the copy form would
  // have shown seeded values in their place — so it refuses instead. Same
  // contract as `unresolvedSegmentPositions`.
  const [metaCreatives, setMetaCreatives] = useState<MetaCreativeSlot[]>(() => initialCreativeState.meta.slots);
  const [metaAssets, setMetaAssets] = useState<MetaAssetsBySlot>(() =>
    creativeDataFromSlots(
      initialCreativeState.meta.slots,
      initialPinnedCreatives,
      production,
      'meta',
    ));
  const [ctvCreatives, setCtvCreatives] = useState<CreativeSlot[]>(() => initialCreativeState.ctv.slots);
  const [ctvData, setCtvData] = useState<Record<string, CtvSlotData>>(() =>
    creativeDataFromSlots(
      initialCreativeState.ctv.slots,
      initialPinnedCreatives,
      production,
      'ctv',
    ));
  const [dspGroups, setDspGroups] = useState<CreativeSlot[]>(() => initialCreativeState.dsp.slots);
  const [dspData, setDspData] = useState<Record<string, DspSlotData>>(() =>
    creativeDataFromSlots(
      initialCreativeState.dsp.slots,
      initialPinnedCreatives,
      production,
      'dsp',
    ));
  // The operator-facing concept is the agency margin; the wire field it maps
  // onto stays `markup_on`, so the local name follows the wire.
  const [markup, setMarkup] = useState(campaign?.markup || false);
  const [editedFlights, setEditedFlights] = useState<Set<string>>(new Set());

  // The campaign's dates and budget are the sole flight's dates and budget until
  // the operator says otherwise. `wasLive` is excluded: a published campaign's
  // flights are the contract its ad sets were built from.
  useEffect(() => {
    if (wasLive) return;
    setFlights((current) => mirrorSoleUntouchedFlight(
      current, editedFlights, startDate, endDate, budget,
    ));
  }, [wasLive, editedFlights, startDate, endDate, budget]);
  const [flights, setFlights] = useState<CampaignFlight[]>(campaign?.flights || []);
  const [savedPlan, setSavedPlan] = useState<CampaignPlan | null>(plan ?? null);
  const [savedFingerprint, setSavedFingerprint] = useState<string | null>(null);
  // Extend flight (2026-10-21) is the one change a published plan takes: its terms are
  // replaced in place from the plan carrying the new flight, never by a remount.
  const applyPlanTerms = usePlanTermsRefresh({
    plan, wasLive, flights, setBudget, setStartDate, setEndDate, setFlights,
    setEditedFlights, setSavedPlan, setSavedFingerprint,
  });
  const [preflightResult, setPreflightResult] = useState<CampaignPreflight | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionNotice, setActionNotice] = useState<string | null>(null);
  const required = useRequiredFeedback();
  const materializedAudiences = useRef(new Map<string, Audience>());
  const audienceIdByVisualGroup = useRef(new Map<string, string>());
  const materializedCreatives = useRef(new Map<string, MaterializedCreative>());
  const saveOperation = useRef<Promise<CampaignPlan> | null>(null);
  // One id for this wizard's campaign, minted once and reused by every create
  // attempt. A fresh id per attempt turns a POST whose response was lost into
  // a second campaign: the first is committed in D1 but nothing in the UI is
  // editing it. Reusing the id makes the retry collide with the campaign that
  // actually landed, which is recoverable.
  const plannedCampaignId = useRef<string | null>(null);
  // The highest revision this wizard has itself tried to write — 1 for the
  // create, one past whatever a recovery read. It is how the create recovery
  // tells its own committed write from another editor's.
  const attemptedRevision = useRef(0);
  const [saveInFlight, setSaveInFlight] = useState(false);

  const createAudience = useCreateAudience();
  const createCreative = useCreateCreative();
  const patchCreative = usePatchCreative();
  const submitCreativeForReview = useSubmitCreativeForReview();
  const approveCreative = useApproveCreative();
  const createCampaign = useCreateCampaign();
  const updateCampaignPlan = useUpdateCampaignPlan();
  const duplicateCampaign = useDuplicateCampaign();
  const retryCampaignSync = useRetryCampaignSync();
  const archiveCampaign = useArchiveCampaign();
  const deleteCampaign = useDeleteCampaign();
  const launchCampaign = useLaunchCampaign();
  const launchWhenReady = useLaunchWhenReady();
  const cancelLaunchWhenReady = useCancelLaunchWhenReady();
  const pauseCampaign = usePauseCampaign();
  const resumeCampaign = useResumeCampaign();
  // New drafts begin with one full-window flight. Existing campaigns use the
  // exact canonical flights returned by D1.
  useEffect(() => {
    if (flights.length === 0 && startDate && endDate) {
      setFlights([{
        id: `flight_${crypto.randomUUID()}`,
        name: 'Flight 1',
        start: startDate,
        end: endDate,
        budget,
      }]);
      setEditedFlights(new Set());
    }
  }, [startDate, endDate]);

  const [open, setOpen] = useState<Record<CreateFlowSection, boolean>>({
    production: !production, obj: false, ch: !campaign && !!production, aud: false, cr: false, prev: false, sched: false, rev: false,
  });
  const toggle = (k: CreateFlowSection) => setOpen({...open, [k]: !open[k]});
  const savedCampaignId = savedPlan?.campaign?.id || campaign?.id || '';
  const preflightQuery = useCampaignPreflight(
    savedCampaignId,
    // Ambient for any saved campaign now, not just while Review is open: the
    // right column reports the path to serving continuously, and that is the
    // question this answers. It polls at 60s and stops itself once ready.
    !!savedCampaignId,
  );
  // The campaign's audience size, and the only number that reaches the
  // cost-per-ticket forecast. Three cases, two of them exactly as before:
  //
  //  1. one slot          — that slot's size, including a lone lookalike;
  //  2. several, none prospecting — the deduped union, as a single-type
  //     multi-audience campaign has always had;
  //  3. any prospecting slot — NO number. `prospect_preview` answers with the
  //     lookalike's modelled size alone, so a union containing one would state
  //     a reach that ignores every other audience, and that number feeds a
  //     spend decision. Counted reach and modelled reach are different grains;
  //     the per-audience gauges say what each one is.
  //
  // The union is built by flattening the already-ENCODED slot roots rather than
  // by concatenating their groups: it reproduces the previous request byte for
  // byte so the KV and query cache keys survive, and two slots that both name a
  // branch `segment-1` cannot collide into a duplicate-id throw.
  const unionAudienceRequest = useMemo(() => {
    if (audienceSlots.length < 2 || anySlotProspecting) return null;
    const encodedSlots = slotEncodings.filter((encoded) => encoded !== null);
    if (encodedSlots.length !== slotEncodings.length) return null;
    const branches = encodedSlots.flatMap((encoded) => encoded.rules.rules ?? []);
    const [first] = encodedSlots;
    if (branches.length === 0 || !first) return null;
    const request: AudiencePreviewInput = {
      rules: { ...first.rules, rules: branches },
      projections: [{ kind: 'count' }],
    };
    return request;
  }, [anySlotProspecting, audienceSlots.length, slotEncodings]);

  const soleSlotRequest = useMemo(() => {
    const [only] = slotEncodings;
    if (audienceSlots.length !== 1 || !only) return null;
    const request: AudiencePreviewInput = { rules: only.rules, projections: [{ kind: 'count' }] };
    return request;
  }, [audienceSlots.length, slotEncodings]);

  // A single prospecting slot keeps its own preview, which is the modelled
  // curve the builder already shows; asking `estimate-size` for it would be the
  // wrong endpoint.
  const campaignSizeRequest = audienceSlots.length === 1 && !anySlotProspecting
    ? soleSlotRequest
    : unionAudienceRequest;
  const campaignSizeEstimate = useAudienceEstimateSize(campaignSizeRequest);

  const audSize = useMemo(() => {
    const [only] = audienceSlots;
    if (audienceSlots.length === 1 && only) {
      // A lone lookalike's size comes from its own modelled preview.
      if (prospectingSlots[0]) return settledAudienceSize(slotPreviews[only.id]);
      return settledAudienceSize({
        count: campaignSizeEstimate.data?.count,
        totalReach: campaignSizeEstimate.data?.total_reach,
        isPending: campaignSizeEstimate.isFetching,
        error: campaignSizeEstimate.error,
      });
    }
    if (anySlotProspecting || !campaignSizeRequest) return null;
    return settledAudienceSize({
      count: campaignSizeEstimate.data?.count,
      totalReach: campaignSizeEstimate.data?.total_reach,
      isPending: campaignSizeEstimate.isFetching,
      error: campaignSizeEstimate.error,
    });
  }, [
    anySlotProspecting,
    audienceSlots,
    campaignSizeEstimate.data?.count,
    campaignSizeEstimate.data?.total_reach,
    campaignSizeEstimate.error,
    campaignSizeEstimate.isFetching,
    campaignSizeRequest,
    prospectingSlots,
    slotPreviews,
  ]);

  // Why the campaign has no single size, when it does not. Stated rather than
  // left as a dash: an absent number with no reason reads as a bug.
  const audienceTypeSummary = [...new Set(audienceSlots.map((slot) => slot.type))].join(' + ');
  const slotAllocations = useMemo(
    () => equalAudienceAllocations(audienceSlots.length),
    [audienceSlots.length],
  );
  const audSizeUnavailableReason = audSize === null && anySlotProspecting && audienceSlots.length > 1
    ? 'no campaign total — a modelled prospect audience and a counted one are different grains'
    : null;

  // Per-audience sizes, which are always available and always comparable to
  // themselves. One line per ad set that will actually run.
  const slotSizeSummaries = useMemo(() => audienceSlots.map((slot, index) => ({
    id: slot.id,
    label: `Audience ${index + 1}`,
    type: slot.type,
    size: settledAudienceSize(slotPreviews[slot.id]),
    prospecting: !!prospectingSlots[index],
  })), [audienceSlots, prospectingSlots, slotPreviews]);

  // ── Pause / Resume + more-actions menu in the edit header ────────────
  // Mirror server status locally so mutation results and undo feedback render
  // without waiting for the route query to refetch.
  const [moreOpen, setMoreOpen] = useState(false);
  const [undoToast, setUndoToast] = useState<StatusUndoToast | null>(null);
  const moreRef = useRef<HTMLDivElement>(null);
  useOutsideClick(moreRef, () => setMoreOpen(false));

  // ── Lock-aware extras ──────────────────────────────────────────────────
  // Budget input flash when user types below the floor (clamps on blur).
  const [budgetFlash, setBudgetFlash] = useState(false);
  // Launch confirmation dialog.
  const [launchOpen, setLaunchOpen] = useState(false);
  const [publishProgress, setPublishProgress] = useState<PublishProgress | null>(null);
  const [audienceSyncGate, setAudienceSyncGate] = useState<AudienceSyncGateState | null>(null);
  const [creativeSyncGate, setCreativeSyncGate] = useState<CreativeSyncGateState | null>(null);
  // Ambient for any saved campaign, not only while the publish modal is open.
  // Cheap (one row plus its stage journal), already terminal-aware, and it is
  // what animates the live step in the right column.
  const campaignSyncQuery = useCampaignSyncStatus(
    publishProgress?.campaignId || savedCampaignId || '',
    {
      enabled: !!(publishProgress || savedCampaignId),
      target: publishProgress?.target ?? deliveryTargetFor(channel),
    },
  );
  // One derivation, two surfaces: the sidebar sequence and the publish modal
  // read the same steps, so they cannot tell different stories about one
  // campaign. Pure and tested in `lib/campaign-publish-sequence`.
  // How many legs the SAVED plan calls for. The journal only gains a row when
  // a leg is about to run, so without this the roll-ups count the work that
  // has started and call it the total — reading "0 of 1" on a two-audience
  // publish, and briefly "1 of 1 ✓" in the gap before the second one begins.
  // The saved plan, not the wizard's live slots: this describes a revision
  // that is already publishing, which unsaved edits do not belong to.
  const publishExpected = useMemo(() => {
    const segments = plan?.audience_segments?.length ?? 0;
    const creativeCount = plan?.creatives?.length ?? 0;
    if (!segments) return undefined;
    return { adSets: segments, ads: creativeCount ? segments * creativeCount : undefined };
  }, [plan?.audience_segments?.length, plan?.creatives?.length]);
  const publishSteps = useMemo(
    () => (savedCampaignId
      ? campaignPublishSequence({
        target: publishProgress?.target ?? deliveryTargetFor(channel),
        preflight: preflightQuery.data,
        sync: campaignSyncQuery.data,
        expected: publishExpected,
        creativeName: (position) => {
          const slot = plan?.creatives?.find((creative) => creative.position === position);
          const creative = slot && availableCreatives.find((row) => row.id === slot.creative_id);
          return creative?.name ?? `creative ${position + 1}`;
        },
      })
      : []),
    [savedCampaignId, publishProgress?.target, channel, preflightQuery.data, campaignSyncQuery.data, publishExpected, plan?.creatives, availableCreatives],
  );
  const publishTarget = publishProgress?.target ?? deliveryTargetFor(channel);
  const publishSettled = campaignSyncQuery.data
    ? campaignSyncSettled(campaignSyncQuery.data, publishTarget)
    : false;
  // The overview row is the same reporting read the Campaigns list makes:
  // gross spend, impressions, pacing and the newest complete delivery day.
  // Only a campaign that has left draft has anything to report.
  // Spend happens only inside the flight, so the window is the flight up to
  // today: never more than the 180 days a campaign may run, so an ended
  // campaign however old stays within the overview's 366-day bound.
  const overviewTo = [todayStr, endDate].reduce((min, day) => (day && day < min ? day : min), todayStr);
  const overviewQuery = useCampaignOverview({
    from: startDate,
    to: overviewTo < startDate ? startDate : overviewTo,
    campaign_id: savedCampaignId || undefined,
    audience_types: [],
    sort: 'needs_attention',
    direction: 'asc',
  }, { enabled: !!savedCampaignId && wasLive });
  const overviewRow = overviewQuery.data?.rows.find((row) => row.campaign_id === savedCampaignId) ?? null;
  // Spend to date and per flight are the campaign's published gross delivery,
  // the figures Reports shows; null until reported, never an estimate.
  const totalSpent = overviewRow?.performance.gross_spend_cents == null
    ? null
    : overviewRow.performance.gross_spend_cents / 100;
  // A failed read is not an unreported campaign: the Schedule step says which.
  const totalSpentFailed = overviewQuery.isError && !overviewQuery.data;
  const flightSpend = useMemo(() => new Map(
    (overviewQuery.data?.flight_spend?.flights ?? []).map((flight) => [
      flight.flight_id,
      flight.gross_spend_cents == null ? null : flight.gross_spend_cents / 100,
    ] as const),
  ), [overviewQuery.data?.flight_spend]);
  const deliveryProps = savedCampaignId && wasLive
    ? {
        target: publishTarget,
        sync: campaignSyncQuery.data,
        startDate,
        endDate,
        todayStr,
        overview: overviewRow,
        deliveryCutoff: overviewQuery.data?.delivery_cutoff ?? null,
        budget,
      }
    : null;
  // A cold failure on either read leaves every step sitting at `pending`, which
  // is indistinguishable from "the publish has not started" — so the panel
  // would claim, confidently and silently, that nothing is wrong.
  const publishReadUnavailable =
    (preflightQuery.isError && !preflightQuery.data)
    || (campaignSyncQuery.isError && !campaignSyncQuery.data);
  const freshCampaignSyncStatus = usePostLaunchSyncSnapshot(publishProgress, campaignSyncQuery);

  // Auto-generated campaign name suggestion
  useEffect(() => {
    if (!name && production && channel) {
      setName(`${production.title.split(' ').slice(0,2).join(' ')} · Conversion · ${{meta:'Meta',ctv:'CTV',dsp:'Display'}[channel]}`);
    }
  }, [production, channel]);

  // A campaign with no single countable size (a modelled prospect audience
  // beside a counted one) still forecasts; the server just omits frequency.
  // Gating the whole request on the size is what left the panel on "Loading
  // historical delivery…" for the life of the tab (2026-09-18).
  const forecastRequest = useMemo(() => {
    if (!production || !channel) return null;
    const budgetCents = Math.round(budget * 100);
    if (!(budgetCents > 0) || !(days >= 1 && days <= 180)) return null;
    const windows = (flights || [])
      .filter((flight) => flight?.start && flight?.end && +flight.budget > 0)
      .map((flight) => ({
        start_date: flight.start,
        end_date: flight.end,
        budget_cents: Math.round(+flight.budget * 100),
      }));
    return {
      production_id: production.id,
      channel,
      audience_size: typeof audSize === 'number' ? audSize : null,
      budget_cents: budgetCents,
      days,
      markup_on: markup,
      campaign_id: savedCampaignId || null,
      // The wizard's own flights, so a draft's projection follows what is on
      // screen; a saved campaign with none sent falls back to its plan's.
      flights: windows.length > 0 ? windows : undefined,
    };
  }, [production, channel, budget, days, audSize, markup, savedCampaignId, flights]);
  const forecastQuery = useCampaignForecast(forecastRequest);
  const forecastNote = !production || !channel
    ? null
    : forecastRequest === null
      ? 'Set a budget and dates to forecast this campaign.'
      : null;

  // Slots whose library creative already passed internal review. CtvDspApprove
  // seeds their placement chips as reviewed on mount, so a reloaded wizard
  // shows the recorded decisions instead of demanding the review again.
  const reviewedSlotIds = useMemo(() => {
    // "Approved" is a revision's state: a v3 draft over an approved v2 must
    // not un-approve the slot, since the plan flies v2.
    const approved = new Set(
      (plan?.creatives ?? [])
        .map((creative) => creative.creative_id)
        .filter((id) =>
          availableCreatives.find((creative) => creative.id === id)?.approved_revision != null),
    );
    return new Set(
      [...ctvCreatives, ...dspGroups]
        .filter((slot) => slot.fromLibrary?.id && approved.has(slot.fromLibrary.id))
        .map((slot) => slot.id),
    );
  }, [plan, availableCreatives, ctvCreatives, dspGroups]);

  const creativeSelections = useMemo(
    () => campaignCreativeSelections(
      channel,
      name,
      production,
      metaCreatives,
      metaAssets,
      ctvCreatives,
      ctvData,
      dspGroups,
      dspData,
    ),
    [
      channel,
      name,
      production,
      metaCreatives,
      metaAssets,
      ctvCreatives,
      ctvData,
      dspGroups,
      dspData,
    ],
  );
  const creativeSelectionCount =
    creativeSelections.libraryIds.length + creativeSelections.drafts.length;
  const unresolvedCreativePositions = unresolvedCreativeSlots(
    channel === 'ctv' ? ctvCreatives : channel === 'dsp' ? dspGroups : metaCreatives,
    creativeSelections.drafts,
  );

  const creativeReviewFingerprint = useMemo(() => JSON.stringify({
    library_ids: creativeSelections.libraryIds,
    drafts: creativeSelections.drafts.map((draft) => ({
      key: draft.key,
      placements: draft.placements.map((placement) => ({
        tag: placement.placementTag,
        asset: localAssetFingerprint(placement.asset),
      })),
    })),
  }), [creativeSelections]);

  // `null` means nothing has changed since mount — the pin below is set on the
  // first commit, so it can only survive a render in which the creatives are
  // still exactly the reviewed ones.
  const previewReviewCurrent = prevConfirmed
    && (reviewedCreativeFingerprint === null
      || reviewedCreativeFingerprint === creativeReviewFingerprint);

  useEffect(() => {
    if (prevConfirmed && reviewedCreativeFingerprint === null) {
      setReviewedCreativeFingerprint(creativeReviewFingerprint);
    }
  }, [prevConfirmed, reviewedCreativeFingerprint, creativeReviewFingerprint]);
  const formFingerprint = useMemo(() => JSON.stringify({
    name: name.trim(),
    production_id: production?.id ?? null,
    channel,
    audience_slots: audienceSlotsFingerprint(audienceSlots),
    creative_selections: {
      library_ids: creativeSelections.libraryIds,
      drafts: creativeSelections.drafts.map((draft) => ({
        key: draft.key,
        name: draft.name,
        media_type: draft.mediaType,
        format: draft.format,
        spec: draft.spec,
        destination_url: draft.destinationUrl,
        placements: draft.placements.map((placement) => ({
          tag: placement.placementTag,
          asset: localAssetFingerprint(placement.asset),
        })),
      })),
      locally_approved: previewReviewCurrent,
    },
    budget_cents: Math.round(budget * 100),
    start_date: startDate,
    end_date: endDate,
    flights: flights.map((flight) => ({
      id: flight.id,
      name: flight.name.trim(),
      start_date: flight.start,
      end_date: flight.end,
      budget_cents: Math.round((+flight.budget || 0) * 100),
    })),
    markup_on: markup,
    starred: campaign?.starred ?? false,
  }), [
    name,
    production?.id,
    channel,
    audienceSlots,
    creativeSelections,
    previewReviewCurrent,
    budget,
    startDate,
    endDate,
    flights,
    markup,
    campaign?.starred,
  ]);

  useEffect(() => {
    if (plan && savedFingerprint === null) setSavedFingerprint(formFingerprint);
  }, [plan, savedFingerprint, formFingerprint]);

  const handleSlotPreviewChange = useCallback((slotId: string, next: AudiencePreviewState) => {
    setSlotPreviews((current) => ({ ...current, [slotId]: next }));
    if (next.rules && !next.error) required.clear('campaign-audience');
  }, [required.clear]);


  // A provider sync the cron lane gave up on (ten attempts inside a TTD
  // gateway window, Sep 2026) reads `failed` until an operator re-drives it.
  const failedSyncLeg = campaignSyncQuery.data
    ? (['ttd', 'meta'] as const).find((leg) => campaignSyncQuery.data?.[leg]?.status === 'failed')
    : undefined;

  const preflight = preflightResult ?? preflightQuery.data;
  // What the sweep is still declining on. It recomputes preflight every minute and keeps
  // nothing, so this is the only place that answer exists.
  const operationalStatus = campaignSyncQuery.data?.operational_status ?? campaign?.operational_status;
  // The header's attention reason is this target's failed sync; its words come
  // with the block, so the line reads as a sentence, not the machine string.
  const publishSyncBlock = campaignSyncQuery.data?.[publishTarget];
  const publishSyncFailure = publishSyncBlock?.status === 'failed' ? publishSyncBlock.failure ?? null : null;
  // Bumped by the header's "publish log" link; the side column opens and scrolls its log on change.
  const [publishLogRequest, setPublishLogRequest] = useState(0);
  // Slot changes and roll-outs still running: the header names them wherever the operator is on the page.
  const inFlightOps = plan ? countInFlightOperations(plan) : 0;
  // Extend flight (campaign lifecycle 2b, 2026-10-21): the journal, whether Extend is on
  // for this campaign's provider, and what the newest request says.
  const flightExtensionsQuery = useCampaignFlightExtensions(savedCampaignId, !!campaign && wasLive);
  const flightExtensions = flightExtensionsQuery.data?.extensions ?? [];
  const extensionSignal = flightExtensionSignal(flightExtensions, Date.now());
  const [extendOpen, setExtendOpen] = useState(false);
  const canLaunch = useCan('campaigns_launch');
  // A queued (TTD) extension reaches the plan when its row commits: read the plan again
  // then, and the refresh above replaces the terms. Only on a change this page saw, so a
  // mount over old committed rows does not refetch the plan for nothing.
  const committedExtensions = flightExtensions
    .filter((row) => row.status === 'committed' || row.status === 'done')
    .map((row) => row.id)
    .join(',');
  const seenCommitted = useRef<string | null>(null);
  useEffect(() => {
    if (!flightExtensionsQuery.data) return;
    const before = seenCommitted.current;
    seenCommitted.current = committedExtensions;
    if (before !== null && before !== committedExtensions && savedCampaignId) {
      void queryClient.invalidateQueries({ queryKey: campaignPlanResource.key(savedCampaignId) });
    }
  }, [committedExtensions, flightExtensionsQuery.data, queryClient, savedCampaignId]);
  const statusLine = campaignStatusLine(operationalStatus, providerLabel(publishTarget), Date.now(), extensionSignal);
  // The third header line exists only for what the chip and the meta line cannot say.
  const statusRowNeeded = !!campaign && !!statusLine && (statusLine.attention || !!statusLine.note || !!statusLine.recoveryBlocked);
  // The Schedule step's current flight reads "Now delivering" only while the
  // provider is (2026-10-19); `wasLive` stays the published lock.
  const flightsDelivering = operationalStatus?.state === 'live' || operationalStatus?.state === 'partially_active';
  const currentRequestedStatus = operationalStatus?.requested_state ?? status;
  // "Extend flight…" in the header and the Schedule banner: a live or paused campaign whose
  // provider's switch is on (both default off), for someone who may launch campaigns.
  const extendAvailable = !!campaign
    && (currentRequestedStatus === 'live' || currentRequestedStatus === 'paused')
    && flightExtensionsQuery.data?.extend_enabled === true
    && canLaunch;
  const lastFlight = [...flights].sort((a, b) => (a.end < b.end ? 1 : a.end > b.end ? -1 : 0))[0] ?? null;
  const headerStatusKind = campaignStatusKind(campaign?.status ?? status, campaign?.auto_launch_requested_at, operationalStatus);
  const armedWaitingOn = headerStatusKind === 'armed'
    ? armedWaitingReason(preflight?.checks)
    : null;
  // Three states, not two. `armedWaitingOn` is null both for "everything passes" and for
  // "preflight has not answered yet", and collapsing those into the optimistic sentence
  // told the operator a blocked campaign was about to launch — on a screen whose only job
  // is saying whether it will.
  const armedStatusLine = armedWaitingOn
    ?? (preflight?.ready === true
      ? 'Every check passed — launching on the next sweep'
      : 'Checking what it is still waiting on…');

  const saving = createCampaign.isPending
    || updateCampaignPlan.isPending
    || createAudience.isPending
    || createCreative.isPending
    || submitCreativeForReview.isPending
    || approveCreative.isPending
    || saveInFlight;

  return {
    navigate,
    queryClient,
    productionRows,
    fetchedAudiences,
    fetchedCreatives,
    availableAudiences,
    availableCreatives,
    initialPinnedCreatives,
    venueNames,
    PRODUCTIONS,
    status,
    setStatus,
    wasLive,
    totalSpent,
    totalSpentFailed,
    flightSpend,
    flightsDelivering,
    todayStr,
    today,
    production,
    setShow,
    channel,
    setChannel,
    metaContractQuery,
    metaContract,
    budget,
    setBudget,
    days,
    setDays,
    initialStartISO,
    initialEndISO,
    maxEndISO,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    name,
    setName,
    savedName,
    setSavedName,
    renaming,
    setRenaming,
    patchCampaign,
    initialAudienceState,
    audienceSlots,
    setAudienceSlots,
    unresolvedSegmentPositions,
    slotPreviews,
    setSlotPreviews,
    slotConfirmed,
    setSlotConfirmed,
    patchSlot,
    slotEncodings,
    prospectingSlots,
    anySlotProspecting,
    audConfirmed,
    setAudConfirmed,
    creConfirmed,
    setCreConfirmed,
    prevConfirmed,
    setPrevConfirmed,
    reviewedCreativeFingerprint,
    setReviewedCreativeFingerprint,
    previewCreativeIds,
    setPreviewCreativeIds,
    schConfirmed,
    setSchConfirmed,
    initialCreativeState,
    metaCreatives,
    setMetaCreatives,
    metaAssets,
    setMetaAssets,
    ctvCreatives,
    setCtvCreatives,
    ctvData,
    setCtvData,
    dspGroups,
    setDspGroups,
    dspData,
    setDspData,
    markup,
    setMarkup,
    editedFlights,
    setEditedFlights,
    flights,
    setFlights,
    savedPlan,
    setSavedPlan,
    savedFingerprint,
    setSavedFingerprint,
    preflightResult,
    setPreflightResult,
    actionError,
    setActionError,
    actionNotice,
    setActionNotice,
    required,
    materializedAudiences,
    audienceIdByVisualGroup,
    materializedCreatives,
    saveOperation,
    plannedCampaignId,
    attemptedRevision,
    saveInFlight,
    setSaveInFlight,
    createAudience,
    createCreative,
    patchCreative,
    submitCreativeForReview,
    approveCreative,
    createCampaign,
    updateCampaignPlan,
    duplicateCampaign,
    retryCampaignSync,
    archiveCampaign,
    deleteCampaign,
    launchCampaign,
    launchWhenReady,
    cancelLaunchWhenReady,
    pauseCampaign,
    resumeCampaign,
    open,
    setOpen,
    toggle,
    savedCampaignId,
    preflightQuery,
    unionAudienceRequest,
    soleSlotRequest,
    campaignSizeRequest,
    campaignSizeEstimate,
    audSize,
    audienceTypeSummary,
    slotAllocations,
    audSizeUnavailableReason,
    slotSizeSummaries,
    moreOpen,
    setMoreOpen,
    undoToast,
    setUndoToast,
    moreRef,
    budgetFlash,
    setBudgetFlash,
    launchOpen,
    setLaunchOpen,
    publishProgress,
    setPublishProgress,
    audienceSyncGate,
    setAudienceSyncGate,
    creativeSyncGate,
    setCreativeSyncGate,
    campaignSyncQuery,
    publishExpected,
    publishSteps,
    publishTarget,
    publishSettled,
    overviewQuery,
    overviewRow,
    deliveryProps,
    publishReadUnavailable,
    freshCampaignSyncStatus,
    forecastRequest,
    forecastQuery,
    forecastNote,
    reviewedSlotIds,
    creativeSelections,
    creativeSelectionCount,
    unresolvedCreativePositions,
    creativeReviewFingerprint,
    previewReviewCurrent,
    formFingerprint,
    handleSlotPreviewChange,
    failedSyncLeg,
    preflight,
    operationalStatus,
    publishSyncBlock,
    publishSyncFailure,
    publishLogRequest,
    setPublishLogRequest,
    inFlightOps,
    flightExtensionsQuery,
    flightExtensions,
    extensionSignal,
    extendOpen,
    setExtendOpen,
    extendAvailable,
    lastFlight,
    applyPlanTerms,
    statusLine,
    statusRowNeeded,
    currentRequestedStatus,
    headerStatusKind,
    armedWaitingOn,
    armedStatusLine,
    saving,
    campaign, plan, setView, audiences, creatives, pinnedCreatives,
  };
}
export type CreateFlowState = ReturnType<typeof useCreateFlow>;
